import axios, { CancelTokenSource } from 'axios';
import { useCallback, useRef, useState } from 'react';
import { api } from 'src/assets/static/ApiRoutes';
import useAsync from 'src/hooks/useAsync';
import axiosInstance from 'src/utils/axios';
import { IAllSuppliers } from './useGetOnlineAndOfflineSuppliers';

const DocMatchStatusArray = [
  'DocNumberNotFound',
  'NoMatch',
  'OcrGeneratedCurrentCompanyExactMatch',
  'OcrGeneratedOtherCompanyExactMatch',
  'NonOcrGeneratedCurrentCompanyExactMatch',
  'NonOcrGeneratedOtherCompanyExactMatch',
  'NonOcrGeneratedCurrentCompanyMultipleExactMatch',
  'NonOcrGeneratedOtherCompanyMultipleExactMatch',
  'OcrGeneratedCurrentCompanyMultipleExactMatch',
  'OcrGeneratedOtherCompanyMultipleExactMatch',
  'OcrGeneratedCurrentCompanyPossibleMatch',
  'OcrGeneratedOtherCompanyPossibleMatch',
  'NonOcrGeneratedCurrentCompanyPossibleMatch',
  'NonOcrGeneratedOtherCompanyPossibleMatch',
] as const;

export const MergeAttachmentStatus = [
  'poOpenInvoiceCancelled',
  'poOpenInvoiceCompletedAndProcessed',
  'poOpenInvoiceNotCompletedOrInvoiceCompletedAndNotProcessed',
  'poClosedInvoiceCompletedAndProcessed',
  'userDoesNotHaveAccess'
] as const;

export type DocMatchStatus = typeof DocMatchStatusArray[number];
export interface ReviewDocumentRequest {
  attachmentId: number;
  companyId: string;
  invoiceNumber?: string;
}

interface CreditMemoPermissionsType {
  canUseNegativeQty: boolean;
  canCreditOverride: boolean;
}

interface MergeAttachmentReviewType {
  mergeAttachmentStatus: number;
  receiptId: number;
  poId: number;
  invoiceNumber: string;
}

export interface ReviewDocumentResponse {
  matchStatus: DocMatchStatus;
  mergeAttachmentReview: MergeAttachmentReviewType | null;
  matchedSuppliers: IAllSuppliers[];
  matchConfidence: string;
  ocrDocNumber: string;
  documentType: string;
  creditMemoPermissions: CreditMemoPermissionsType;
}

const useReviewDocumentApi = () => {
  const reqRef = useRef<CancelTokenSource>();
  const [initialReviewDocValidation, setInitialReviewDocValidation] = useState(
    {} as ReviewDocumentResponse
  );
  const call = useCallback(async (body?: ReviewDocumentRequest) => {
    try {
      if (!body) return {} as ReviewDocumentResponse;
      const req = axios.CancelToken.source();
      if (reqRef.current) {
        reqRef.current.cancel();
      }
      reqRef.current = req;
      const res = await axiosInstance.post(api.reviewDocument(), body, { cancelToken: req.token });
      return res.data;
    } catch (error) {
      throw error;
    }
  }, []);

  const { isLoading, execute, value, setValue, error } = useAsync(
    call,
    false,
    undefined,
    {} as ReviewDocumentResponse
  );

  return {
    reviewDocument: execute,
    isReviewDocumentLoading: isLoading,
    reqRef,
    reviewDocumentValidations: value,
    setReviewDocumentValidations: setValue,
    initialReviewDocValidation,
    setInitialReviewDocValidation,
    reviewDocumentError: error,
  };
};

export default useReviewDocumentApi;
